import { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const n = (x) => Number(x).toLocaleString();
const word = (x, w) => `${w}${x === 1 ? "" : "s"}`;

export function codeSummary(c) {
  const used = c.times_redeemed;
  const u = used == null ? "–" : n(used);
  if (c.rules !== 2) {
    return `${c.percent_off}% off first payment · ${c.limit ? `${u} of ${n(c.limit)} uses` : `${u} ${word(used, "use")}`}`;
  }
  const head = `${c.percent_off}% off for ${n(c.duration_months)} ${word(c.duration_months, "month")}`;
  if (!c.limit) return `${head} · ${u} ${word(used, "customer")} redeemed · Unlimited`;
  const rem = c.remaining == null ? "–" : n(c.remaining);
  return `${head} · ${u} of ${n(c.limit)} customers redeemed · ${rem} ${word(c.remaining, "spot")} remaining`;
}

const STATUS = {
  active: ["Active", "text-emerald-300 border-emerald-400/30 bg-emerald-500/10"],
  full: ["Full", "text-amber-300 border-amber-400/30 bg-amber-500/10"],
  expired: ["Expired", "text-neutral-400 border-white/10 bg-white/5"],
  off: ["Switched off", "text-neutral-400 border-white/10 bg-white/5"],
};

export function StatusBadge({ c }) {
  const [label, cls] = STATUS[c.status] || STATUS.off;
  return <span className={`text-[11px] px-2 py-0.5 rounded-full border ${cls}`} data-testid={`discount-status-${c.code}`}>{label}</span>;
}

export function TermsFields({ f, setF, used, prefix, stacked }) {
  return (
    <div className={`grid gap-3 ${stacked ? "" : "sm:grid-cols-2"}`}>
      <label className="space-y-1.5"><span className="text-xs text-neutral-500">Discount duration (months)</span>
        <Input type="number" min={1} step={1} inputMode="numeric" value={f.duration_months} placeholder="e.g. 6"
          onChange={(e) => setF({ ...f, duration_months: e.target.value })} className="bg-white/5 border-white/10" data-testid={`${prefix}-duration-input`} />
        <span className="block text-[11px] text-neutral-500">How many months each customer receives this discount. This is separate from the expiry date.</span>
      </label>
      <div className="space-y-1.5"><span className="text-xs text-neutral-500">Maximum customers</span>
        <div className="flex items-center gap-3">
          <Input type="number" min={1} max={1000000} step={1} inputMode="numeric" disabled={f.unlimited}
            value={f.unlimited ? "" : f.max_customers} placeholder={f.unlimited ? "Unlimited" : "e.g. 100"}
            onChange={(e) => setF({ ...f, max_customers: e.target.value })} className="bg-white/5 border-white/10 min-w-[7rem] flex-1" data-testid={`${prefix}-max-customers-input`} />
          <label className="flex items-center gap-2 text-sm text-neutral-300 whitespace-nowrap cursor-pointer">
            <Checkbox checked={f.unlimited} onCheckedChange={(v) => setF({ ...f, unlimited: !!v })} data-testid={`${prefix}-unlimited-checkbox`} /> Unlimited customers
          </label>
        </div>
        <span className="block text-[11px] text-neutral-500" data-testid={`${prefix}-max-help`}>
          {used ? `${n(used)} already redeemed, so the maximum can't go below ${n(used)}.` : "Each customer can redeem it once. Only completed payments use a spot."}
        </span>
      </div>
    </div>
  );
}

const initial = (c) => ({ duration_months: c.duration_months ?? "", max_customers: c.limit ?? "", unlimited: c.limit == null });

export function EditCode({ c, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(initial(c));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await http.put(`/admin/discounts/${c.code}`, { duration_months: f.duration_months, max_customers: f.unlimited ? null : f.max_customers, unlimited: f.unlimited });
      toast.success(`Updated ${c.code}`);
      setOpen(false);
      onSaved(data);
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  const expired = c.status === "expired";
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setF(initial(c)); }}>
      <DialogTrigger asChild>
        <button disabled={expired} title={expired ? "Expired codes can't be edited" : "Edit duration and maximum customers"} data-testid={`discount-edit-${c.code}`}
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border border-white/10 text-neutral-400 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-40">
          <Pencil className="h-3 w-3" /> Edit
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0E1626] border-[#1B2740] text-white max-w-lg" data-testid={`discount-edit-dialog-${c.code}`}>
        <DialogHeader>
          <DialogTitle>Edit {c.code}</DialogTitle>
          <DialogDescription className="text-neutral-400">New terms apply to future customers only. Customers who already redeemed keep the months they were given, and the code they type stays {c.code}.</DialogDescription>
        </DialogHeader>
        {c.rules !== 2 && (
          <p className="text-xs rounded-lg border border-amber-400/20 bg-amber-500/10 text-amber-200 px-3 py-2" data-testid="discount-edit-legacy-note">
            Saving moves this older code onto the new rules for future customers: the months you set, monthly plans only, and one redemption per customer.
          </p>
        )}
        <TermsFields prefix="discount-edit" stacked f={f} setF={setF} used={c.times_redeemed} />
        <DialogFooter className="gap-2">
          <button onClick={() => setOpen(false)} className="h-9 px-4 rounded-full border border-white/15 text-sm hover:bg-white/5" data-testid="discount-edit-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="h-9 px-5 rounded-full bg-sky-400 text-white text-sm font-medium hover:bg-sky-500 disabled:opacity-50" data-testid="discount-edit-save">Save changes</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteCode({ code, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await http.delete(`/admin/discounts/${code}`);
      toast.success("Code deleted");
      onDeleted();
    } catch (e) { toast.error(formatError(e)); setBusy(false); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button disabled={busy} data-testid={`discount-delete-${code}`} title="Delete code"
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border border-white/10 text-neutral-400 hover:text-red-300 hover:border-red-400/30 hover:bg-red-500/10 transition-colors disabled:opacity-50">
          <Trash2 className="h-3 w-3" /> Delete
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#0E1626] border-[#1B2740] text-white" data-testid={`discount-delete-dialog-${code}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {code}?</AlertDialogTitle>
          <AlertDialogDescription className="text-neutral-400">New customers won't be able to use it. Customers who already redeemed it keep their discount. This can't be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-transparent border-white/15 text-white hover:bg-white/5" data-testid={`discount-delete-cancel-${code}`}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={remove} className="bg-red-500 text-white hover:bg-red-400" data-testid={`discount-delete-confirm-${code}`}>Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
